import React from "react";
import styled, { css } from "styled-components";

import {
  breakpoints,
  colors,
  fontFamily,
  sectionRhythm,
} from "../../../theme";
import { PROJECT_JIFFY_DESIGN_LAB } from "../../utils/constants/projects";
import ContactMeSection from "../contact-me-section";
import Testimonials from "../reviews-section/Testimonials";
import ContainerScroll from "../../components/container-scroll";
import { ProjectHero, caseStudyBodyStyles } from "./project-page-media";
import { POINTER_HOVER_MEDIA } from "../../styles/pointer-hover";
import { getLinkClassName } from "../../styles/global-styles";

const PageContainer = styled.div`
  width: calc(100vw - 48px);
  padding-top: ${sectionRhythm.paddingY.mobile}px;
  margin: 0 auto;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 65%;
    max-width: 800px;
    padding-top: ${sectionRhythm.paddingY.desktop}px;
    margin: 0 auto;
  }
  @media only screen and (min-width: 900px) {
    width: 60%;
  }
`;

const Title = styled.h1`
  margin: 0 0 16px;
  font-family: ${fontFamily.extraBold};
  font-size: 24px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 44px;
    margin: 0 0 24px;
  }
`;

const ContentImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  border-radius: 18px;
  background: ${colors.white};
  margin: 1.5rem 0 0.5rem;
`;

const ImageCaption = styled.figcaption`
  display: block;
  font-size: 14px;
  text-align: center;
  color: ${colors.grey.darkMedium};
  margin-bottom: 1.5rem;
`;

const OverviewSection = styled.div`
  margin-top: 2rem;
  margin-bottom: 0;
  display: grid;
  gap: 1.5rem;
`;

const OverviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
`;

const OverviewItem = styled.div`
  flex: 1;
  min-width: 140px;
`;

const OverviewLabel = styled.div`
  font-size: 14px;
  color: ${colors.grey.darkMedium};
  margin-bottom: 4px;
`;

const OverviewValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;

  a {
    color: inherit;
    word-break: break-all;

    @media ${POINTER_HOVER_MEDIA} {
      &:hover {
        color: ${colors.linkHover.light};
        text-decoration: underline;
      }
    }
  }
`;

const Body = styled.div`
  ${caseStudyBodyStyles}
`;

const boxHelperTypography = css`
  font-size: 14px;
  line-height: 1.45;
  font-weight: 600;
  color: ${colors.grey.darkMedium};
`;

const diagramHelperText = css`
  ${boxHelperTypography}
  display: block;
`;

const DecisionSection = styled.div`
  margin-top: 0;
`;

const DecisionBlock = styled.div`
  margin-top: 0;
  padding-top: 2rem;

  &:first-child {
    padding-top: 0;
  }

  & > h5 {
    margin-top: 0;
  }
`;

const DecisionTitle = styled.h5`
  margin: 0 0 0.75rem;
  font-family: ${fontFamily.extraBold};
  font-size: 16px;
  line-height: 1.3;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 18px;
  }
`;

const DecisionQuestion = styled.p`
  margin: 1.25rem 0 1.5rem;
  font-weight: 600;
`;

const DecisionComparison = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin: 2rem 0;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(
      ${(props) => props.$columns || 2},
      minmax(0, 1fr)
    );
  }
`;

const DecisionCard = styled.div`
  padding: 1.25rem;
  border: 1px solid ${colors.grey.light};
  border-radius: 10px;
  background: ${colors.white};

  h6 {
    margin: 0 0 0.75rem;
    font-family: ${fontFamily.extraBold};
    font-size: 15px;
    line-height: 1.3;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 16px;
    }
  }

  ul,
  ol {
    margin: 0;
    padding-left: 1.25rem;
    list-style: none;
  }

  li {
    ${boxHelperTypography}
    display: list-item;
    margin: 0 0 0.45rem 1rem;
    padding: 0;
    letter-spacing: -0.003em;
    list-style-position: outside;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 14px;
      line-height: 1.45;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul > li {
    list-style-type: disc;
  }

  ol > li {
    list-style-type: decimal;
  }
`;

const ArchitectureSplit = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin: 2rem 0 2.5rem;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2.5rem;
  }
`;

const ArchitectureBranch = styled.div``;

const ArchitectureLabel = styled.div`
  margin-bottom: 0.8rem;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-align: center;
  text-transform: uppercase;
  color: ${colors.grey.darkMedium};
`;

const ArchitectureStack = styled.div`
  display: grid;
  gap: 0.6rem;
`;

const ArchitectureStackItem = styled.div`
  padding: 0.85rem 1rem;
  border: 1px solid ${colors.grey.light};
  border-radius: 8px;
  background: ${colors.white};

  strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
  }

  small {
    ${diagramHelperText}
    margin-top: 0.2rem;
  }
`;

const PlacementSplit = () => (
  <ArchitectureSplit>
    <ArchitectureBranch>
      <ArchitectureLabel>Artboard UI</ArchitectureLabel>
      <ArchitectureStack>
        <ArchitectureStackItem>
          <strong>Konva stage</strong>
          <small>Blank, printable area, artwork transform</small>
        </ArchitectureStackItem>
        <ArchitectureStackItem>
          <strong>Refine tools</strong>
          <small>Crop, erase, pan, negative space</small>
        </ArchitectureStackItem>
        <ArchitectureStackItem>
          <strong>React Aria controls</strong>
          <small>Toolbar, dialogs, and form UI around the canvas</small>
        </ArchitectureStackItem>
      </ArchitectureStack>
    </ArchitectureBranch>
    <ArchitectureBranch>
      <ArchitectureLabel>Behind the artboard</ArchitectureLabel>
      <ArchitectureStack>
        <ArchitectureStackItem>
          <strong>ActionCable</strong>
          <small>Ordered DTF processing progress</small>
        </ArchitectureStackItem>
        <ArchitectureStackItem>
          <strong>Server jobs</strong>
          <small>Artwork enhancement off the main request</small>
        </ArchitectureStackItem>
        <ArchitectureStackItem>
          <strong>GraphQL projects</strong>
          <small>Save, resume, and Made add-to-cart</small>
        </ArchitectureStackItem>
      </ArchitectureStack>
    </ArchitectureBranch>
  </ArchitectureSplit>
);

const JiffyDesignLabProjectSection = () => {
  const {
    client,
    services,
    industry,
    techStack,
    links,
    heading,
    media,
    roles,
    year,
  } = PROJECT_JIFFY_DESIGN_LAB;
  const yearLabel = year.length > 1 ? `${year[0]}-${year[1]}` : year[0];

  return (
    <>
      <PageContainer>
        <ContainerScroll
          titleComponent={<Title>{heading}</Title>}
          role={roles.join(", ")}
          year={yearLabel}
        >
          <ProjectHero
            src={media.cover.desktop}
            alt="Jiffy Design Lab apparel customization"
          />
        </ContainerScroll>

        <OverviewSection>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Client</OverviewLabel>
              <OverviewValue>{client.name}</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Country</OverviewLabel>
              <OverviewValue>{client.country}</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Services provided</OverviewLabel>
              <OverviewValue>{services.join(", ")}</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Industry</OverviewLabel>
              <OverviewValue>{industry}</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewItem>
            <OverviewLabel>Tech stack</OverviewLabel>
            <OverviewValue>{techStack.join(", ")}</OverviewValue>
          </OverviewItem>
          {links?.project ? (
            <OverviewItem>
              <OverviewLabel>Project link</OverviewLabel>
              <OverviewValue>
                <a
                  href={links.project}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {links.project}
                </a>
              </OverviewValue>
            </OverviewItem>
          ) : null}
        </OverviewSection>

        <Body>
          <section>
            <h4>About the product</h4>
            <p>
              Design Lab is the customization flow on Jiffy&apos;s Made path.
              Customers upload artwork, pick a blank and color, choose sizes,
              place the design on front and back, refine it, and add the project
              to cart.
            </p>
            <p>
              I worked on this lab as a full-page React app inside the Rails
              storefront. Rails still owns session and checkout. The lab is
              React. Artwork prep runs in server jobs. Transfers Lab uses the
              same React mount on a sibling route.
            </p>
            {media.artboard ? (
              <figure>
                <ContentImage
                  src={media.artboard}
                  alt="Jiffy Design Lab artboard"
                />
                <ImageCaption>
                  Placement and preview on a blank in the Made customization
                  flow.
                </ImageCaption>
              </figure>
            ) : null}
          </section>

          <section>
            <h4>Engineering decisions</h4>

            <DecisionSection>
              <DecisionBlock>
                <DecisionTitle>
                  1. A React lab inside the Rails storefront
                </DecisionTitle>

                <p>
                  Catalog pages on Jiffy use Rails HTML and Vue islands. Design
                  Lab is a multi-step tool with its own routing, a large
                  TypeScript codebase, and providers for artwork and processing.
                  Putting that on a normal storefront page as another Vue island
                  would have been a bad fit.
                </p>

                <DecisionQuestion>
                  The lab needs its own app. The store still needs Rails for
                  session and commerce.
                </DecisionQuestion>

                <p>
                  The lab lives on a dedicated <code>/design-lab</code> route
                  and layout. Rails still owns auth, experiments, AI credits,
                  project access, and session analytics. The view dumps that
                  config into a mount node. The <code>jiffy/react</code> pack
                  boots a Feature-Sliced React app with its own router, desktop
                  and mobile shells, and XState for create, resume, or continue
                  a project.
                </p>

                <DecisionComparison>
                  <DecisionCard>
                    <h6>Dedicated React page</h6>
                    <ul>
                      <li>Own routing and Feature-Sliced structure</li>
                      <li>Rails still supplies session and config</li>
                      <li>Clear boundary from Vue catalog islands</li>
                      <li>Transfers Lab can share the same mount</li>
                    </ul>
                  </DecisionCard>
                  <DecisionCard>
                    <h6>Vue island on a catalog page</h6>
                    <ul>
                      <li>Matches cart and PDP patterns</li>
                      <li>Weak fit for multi-step client routing</li>
                      <li>Harder to isolate a large TypeScript app</li>
                      <li>Too much work for a Vue island</li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  I shipped lab features in that Feature-Sliced tree while Rails
                  stayed the host for session and checkout. React Aria and
                  Tailwind cover the UI around the canvas. That stack is
                  separate from the catalog&apos;s Vue.
                </p>
              </DecisionBlock>

              <DecisionBlock>
                <DecisionTitle>
                  2. Artwork prep behind the artboard
                </DecisionTitle>

                <p>
                  Uploading a file is not enough for Made. Artwork has to be
                  validated, processed, priced, and prepared. That can take a
                  while. A single blocking request would freeze the lab on a
                  heavy file.
                </p>

                <DecisionQuestion>
                  Artwork prep cannot freeze the lab.
                </DecisionQuestion>

                <p>
                  The artboard talks to an ActionCable DTF channel. Steps run in
                  this order:
                </p>
                <ol>
                  <li>Upload</li>
                  <li>Validate</li>
                  <li>Process</li>
                  <li>Lighter process pass</li>
                  <li>Background metadata</li>
                  <li>Price</li>
                  <li>Delivery</li>
                  <li>Preview</li>
                </ol>
                <p>
                  Progress comes back over the cable. Server jobs do the heavy
                  work. The browser tracks progress. It does not run the print
                  algorithms.
                </p>

                <DecisionComparison>
                  <DecisionCard>
                    <h6>ActionCable + server jobs</h6>
                    <ul>
                      <li>UI stays usable during long jobs</li>
                      <li>Steps stay ordered and visible</li>
                      <li>Heavy work stays on the server</li>
                      <li>Frontend has to handle progress and failure</li>
                    </ul>
                  </DecisionCard>
                  <DecisionCard>
                    <h6>One sync request</h6>
                    <ul>
                      <li>Easier to think about</li>
                      <li>Timeouts on heavy artwork</li>
                      <li>Frozen UI while waiting</li>
                      <li>Poor fit for multi-step prep</li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  Frontend work meant progress, failure states, and coming back
                  to a project. Save and resume go through GraphQL. A UUID URL
                  lets someone leave and pick the project up later.
                </p>
              </DecisionBlock>

              <DecisionBlock>
                <DecisionTitle>
                  3. WYSIWYG placement with Konva
                </DecisionTitle>

                <p>
                  Customers need to see the design on the blank before cart:
                  printable area, sizing, front and back. That needs a canvas
                  with transforms and refine tools. A static preview is not
                  enough.
                </p>

                <DecisionQuestion>
                  Placement has to show print limits. It does not need to be a
                  full graphics editor.
                </DecisionQuestion>

                <p>
                  The artboard uses Konva and react-konva for the stage, layers,
                  artwork group, transformer, and printable area. Refine tools
                  cover crop, erase, and pan. Some extra tools sit behind
                  experiments. Controls around the canvas use React Aria. The
                  canvas itself stays Konva.
                </p>

                <PlacementSplit />

                <DecisionComparison>
                  <DecisionCard>
                    <h6>Konva / react-konva</h6>
                    <ul>
                      <li>Solid React bindings for 2D placement</li>
                      <li>Fits transform and refine overlays</li>
                      <li>Mature enough for a production artboard</li>
                      <li>Another specialized dependency to keep</li>
                    </ul>
                  </DecisionCard>
                  <DecisionCard>
                    <h6>DOM / CSS only</h6>
                    <ul>
                      <li>No canvas library</li>
                      <li>Weak for erase, crop, and print overlays</li>
                      <li>Harder printable-area rules</li>
                      <li>Not enough for Made placement</li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  Most of my work was keeping placement inside the printable
                  area, stopping refine tools from fighting the main transform,
                  and keeping the artboard on the same GraphQL project and cart
                  path.
                </p>
              </DecisionBlock>
            </DecisionSection>
          </section>

          <section>
            <h4>Outcome</h4>
            <p>
              Design Lab is one guided Made flow: intake, product, sizing,
              placement, and cart. Artwork is prepared through a stepped
              pipeline while the UI stays usable, and placement is WYSIWYG on
              the blank before checkout.
            </p>
            <p>
              Projects can be resumed from a UUID URL through GraphQL. Transfers
              Lab is a sibling route on the same React app. I worked inside that
              setup. The catalog Vue islands were not the place for this.
            </p>
            <p>
              Packs, WebView, and Vue islands on the catalog are covered in the{" "}
              <a href="/projects/jiffy" className={getLinkClassName()}>
                Jiffy storefront case study
              </a>
              .
            </p>
          </section>

          <section>
            <h4>Testimonials</h4>
            <Testimonials showMore={false} reviews={client.reviews} />
          </section>
        </Body>
      </PageContainer>
      <ContactMeSection />
    </>
  );
};

export default JiffyDesignLabProjectSection;
